import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { SubmitHandler } from 'react-hook-form'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { ShiftTemplate, UpdateShiftTemplateParams } from '@/core/models/shift.model'
import { ScheduleRecurrenceType } from '@/core/models/schedule.model'
import { useUpdateShiftTemplateMutation } from '@/core/services/planning'
import { useGetSpecialityOptionsQuery } from '@/core/services/speciality-option'
import { getApiErrorMessage } from '@/utils/errors'
import { browserTimezone, templateMinutesToLocal } from '@/utils/date'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

import { ShiftTemplateForm, ShiftTemplateFormData } from './ShiftTemplateForm'

dayjs.extend(utc)

interface EditShiftTemplateDialogProps {
  shiftTemplate: ShiftTemplate
  open: boolean
  onClose: () => void
  planningId: string
  onSuccess?: () => void
}

const formId = 'edit-shift-template-form'

export function EditShiftTemplateDialog({ open, onClose, shiftTemplate, planningId, onSuccess }: EditShiftTemplateDialogProps) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [search, setSearch] = useState('')

  const { specialityOptions } = useGetSpecialityOptionsQuery(
    {
      smartSearch: search,
    },
    {
      selectFromResult: ({ data }) => ({ specialityOptions: data?.map((option) => ({ label: option.name, value: option.name })) ?? [] }),
      refetchOnMountOrArgChange: true,
    }
  )
  const [updateShiftTemplate, { isLoading: isUpdating }] = useUpdateShiftTemplateMutation()

  // Prepare default values from the existing shift template
  const initialValues: ShiftTemplateFormData = useMemo(
    () => ({
      location: shiftTemplate.location || '',
      consultationReason: shiftTemplate.consultationReason || '',
      requiredStaffCount: shiftTemplate.requiredStaffCount || 1,
      duration: shiftTemplate.duration || 30,
      // Show the template's time on the viewer's local clock. Templates with a
      // timezone store wall-clock minutes in that zone; legacy ones store
      // minutes-from-UTC-midnight — templateMinutesToLocal handles both.
      startTime: templateMinutesToLocal(shiftTemplate.startTime, shiftTemplate.timezone, shiftTemplate.startDate),
      endTime: templateMinutesToLocal(shiftTemplate.endTime, shiftTemplate.timezone, shiftTemplate.startDate),
      recurrenceRule: shiftTemplate.recurrenceRule ?? {
        daysOfWeek: [],
        datesOfMonth: [],
        monthsOfYear: [],
        recurrenceType: ScheduleRecurrenceType.DAILY,
        separationCount: 1,
      },
      isRecurring: !!shiftTemplate.recurrenceRule,
      date: shiftTemplate.startDate ? new Date(shiftTemplate.startDate) : undefined,
    }),
    [shiftTemplate]
  )

  const handleFormSubmit: SubmitHandler<ShiftTemplateFormData> = async (data) => {
    try {
      const updateParams: UpdateShiftTemplateParams = {
        location: data.location,
        consultationReason: data.consultationReason,
        requiredStaffCount: data.requiredStaffCount,
        duration: data.duration,
        // Save the picked local minutes with the editor's timezone (this also
        // upgrades legacy UTC-minute templates to the timezone-aware model).
        startTime: data.startTime,
        endTime: data.endTime,
        timezone: browserTimezone(),
        ...(data.isRecurring
          ? {
              recurrenceRule: {
                ...shiftTemplate.recurrenceRule,
                daysOfWeek: data.recurrenceRule.daysOfWeek.filter((day): day is number => day !== undefined),
                datesOfMonth: data.recurrenceRule.datesOfMonth,
                monthsOfYear: data.recurrenceRule.monthsOfYear,
                recurrenceType: data.recurrenceRule.recurrenceType,
                separationCount: data.recurrenceRule.separationCount,
              },
            }
          : {
              startDate: data.date ? dayjs(data.date).startOf('day').toISOString() : undefined,
              endDate: data.date ? dayjs(data.date).startOf('day').toISOString() : undefined,
            }),
      }

      await updateShiftTemplate({
        planningId,
        shiftTemplateId: shiftTemplate._id,
        updateShiftTemplateParams: updateParams,
      }).unwrap()

      enqueueSnackbar(t('page.planning.template.edit.success', { defaultValue: 'Shift template updated successfully' }), { variant: 'success' })

      onSuccess?.()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.planning.template.edit.error', { defaultValue: 'Failed to update shift template' })), {
        variant: 'error',
      })
    }
  }

  const handleSearchSpeciality = useCallback(
    (search: string) => {
      setSearch(search)
    },
    [setSearch]
  )

  return (
    <Dialog hideBackdrop open={open} onClose={onClose} fullScreen={isMobile} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('page.planning.shiftTemplate.edit.title', { defaultValue: 'Edit Shift Template' })}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ShiftTemplateForm
          formId={formId}
          initialValues={initialValues}
          onSearchSpeciality={handleSearchSpeciality}
          onSubmit={handleFormSubmit}
          specialityOptions={specialityOptions}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:button.cancel.label')}</Button>
        <Button form={formId} variant="contained" color="primary" type="submit" disabled={isUpdating}>
          {isUpdating ? t('common:button.saving.label') : t('common:button.save.label')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
